import axios, { AxiosInstance } from 'axios';

import { config as productConfig } from '@/src/config';
export type MaxUnitsPeriod = 'month' | 'year' | 'quarter' | 'week' | 'day' | 'episode';
/** TRUS-709: shared = everyone on the program sees all its members; individual = only assigned ones. */
export type CaseloadModel = 'shared' | 'individual';

export interface Program {
  id: number;
  cbo_id: number;
  name: string;
  program_type: string;
  description?: string;
  is_active: boolean;
  start_date?: string;
  end_date?: string;
  capacity?: number;
  current_enrollment: number;
  funding_source?: string;
  billing_code?: string;
  reimbursement_rate?: number;
  eligibility_criteria?: Record<string, any>;
  services_offered?: string[];
  program_manager_name?: string;
  program_manager_email?: string;
  program_manager_phone?: string;
  created_ts: string;
  created_by?: number;
  max_units_period?: MaxUnitsPeriod;
  /**
   * Nav tab the program switcher opens when this program is selected (TRUS-548).
   * Null leaves the user where they are — the switcher never removes a page, so a
   * program can change where you land but not what you can reach.
   */
  default_nav_tab?: string | null;
  /** Who on the program sees its members (TRUS-709). Absent on older servers. */
  caseload_model?: CaseloadModel | null;
  /**
   * The health plan this program bills (TRUS-725), chosen once in program setup.
   * MIF import and scheduled reports read the plan from here. Null = not chosen.
   */
  payer_id?: number | null;
  payer_name?: string | null;
  /** payers.payer_id, e.g. 'HNET', 'SFHP', 'MEDI-CAL'. */
  payer_key?: string | null;
  payer_type?: string | null;
}

/** A health plan the program-setup picker offers (GET /api/programs/payers). */
export interface ProgramPayer {
  id: number;
  payer_name: string;
  payer_key: string | null;
  payer_type: string | null;
  payer_category: string | null;
  /** Lower-cased payer key, the plan_key the MIF import sends (SFHP -> 'sfhp'). */
  mif_plan_key: string | null;
}

/** The MIF plan_key for a program's chosen payer, or null when no plan is chosen. */
export function mifPlanKeyForProgram(program?: Pick<Program, 'payer_key'> | null): string | null {
  const key = (program?.payer_key ?? '').trim().toLowerCase();
  return key || null;
}

/** True only when the program's chosen plan is the given payer key (case-insensitive). */
export function programBillsPayer(program: Pick<Program, 'payer_key'> | null | undefined, payerKey: string): boolean {
  return (program?.payer_key ?? '').trim().toUpperCase() === payerKey.toUpperCase();
}

export interface ProgramCreate {
  name: string;
  program_type: string;
  description?: string;
  is_active?: boolean;
  start_date?: string;
  end_date?: string;
  capacity?: number;
  funding_source?: string;
  billing_code?: string;
  reimbursement_rate?: number;
  eligibility_criteria?: Record<string, any>;
  services_offered?: string[];
  program_manager_name?: string;
  program_manager_email?: string;
  program_manager_phone?: string;
  max_units_period?: MaxUnitsPeriod;
  default_nav_tab?: string | null;
  /** TRUS-725: payers.id; null clears the plan. */
  payer_id?: number | null;
}

export interface ProgramType {
  value: string;
  label: string;
  description: string;
}

export interface ProgramCategory {
  id: number;
  program_id: number;
  category: string;
  is_required: boolean;
  display_order: number;
  created_at: string;
}

export interface ProgramCategoryCreate {
  category: string;
  is_required?: boolean;
  display_order?: number;
}

class ProgramsAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token interceptor
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (cboId) {
        config.headers['X-CBO-ID'] = cboId;
      }

      return config;
    });
  }

  async listPrograms(params?: {
    program_type?: string;
    is_active?: boolean;
  }): Promise<Program[]> {
    const response = await this.client.get<Program[]>('/api/programs', { params });
    return response.data;
  }

  async getProgram(programId: number): Promise<Program> {
    const response = await this.client.get<Program>(`/api/programs/${programId}`);
    return response.data;
  }

  async createProgram(data: ProgramCreate): Promise<Program> {
    // Convert empty strings to null for optional date fields
    const cleanedData = {
      ...data,
      start_date: data.start_date === '' ? null : data.start_date,
      end_date: data.end_date === '' ? null : data.end_date,
    };
    const response = await this.client.post<Program>('/api/programs', cleanedData);
    return response.data;
  }

  async updateProgram(programId: number, data: Partial<ProgramCreate>): Promise<Program> {
    // Convert empty strings to null for optional date fields
    const cleanedData = {
      ...data,
      start_date: data.start_date === '' ? null : data.start_date,
      end_date: data.end_date === '' ? null : data.end_date,
    };
    const response = await this.client.put<Program>(`/api/programs/${programId}`, cleanedData);
    return response.data;
  }

  /** TRUS-709: change only the program's caseload model (PATCH merges it). */
  async setCaseloadModel(programId: number, caseloadModel: CaseloadModel): Promise<Program> {
    const response = await this.client.patch<Program>(`/api/programs/${programId}`, {
      caseload_model: caseloadModel,
    });
    return response.data;
  }

  async deleteProgram(programId: number): Promise<void> {
    await this.client.delete(`/api/programs/${programId}`);
  }

  /** Health plans for the org's state (default CA), Medi-Cal FFS included (TRUS-725). */
  async listPayers(state?: string): Promise<{ state: string; payers: ProgramPayer[] }> {
    const response = await this.client.get<{ state: string; payers: ProgramPayer[] }>('/api/programs/payers', {
      params: state ? { state } : undefined,
    });
    return response.data;
  }

  async getProgramTypes(): Promise<ProgramType[]> {
    const response = await this.client.get<ProgramType[]>('/api/programs/types/list');
    return response.data;
  }

  // Program Category Management
  async getProgramCategories(programId: number): Promise<ProgramCategory[]> {
    const response = await this.client.get<ProgramCategory[]>(`/api/programs/${programId}/categories`);
    return response.data;
  }

  async addProgramCategory(programId: number, data: ProgramCategoryCreate): Promise<ProgramCategory> {
    const response = await this.client.post<ProgramCategory>(`/api/programs/${programId}/categories`, data);
    return response.data;
  }

  async updateProgramCategoriesBulk(programId: number, categories: ProgramCategoryCreate[]): Promise<ProgramCategory[]> {
    const response = await this.client.put<ProgramCategory[]>(`/api/programs/${programId}/categories/bulk`, categories);
    return response.data;
  }

  async deleteProgramCategory(programId: number, categoryId: number): Promise<void> {
    await this.client.delete(`/api/programs/${programId}/categories/${categoryId}`);
  }
}

export const programsAPI = new ProgramsAPI();
